'use client';
import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const testimonials = [
{
  name: 'Megan Torres',
  role: 'WFH Product Manager',
  location: 'Calgary, Alberta',
  quote: 'I could see my dirty RAV4 from my desk every single day. YYC Shine showed up Tuesday morning, I made two calls, and by lunch it looked like I just drove it off the lot. The ceramic tier is worth every penny.',
  rating: 5,
  tier: 'Ceramic Shield · $99',
  avatar: "https://images.unsplash.com/photo-1663507897721-c6c216a8fb28",
  avatarAlt: 'Megan Torres, smiling woman with brown hair in a bright home office setting',
  featured: true
},
{
  name: 'David Kim',
  role: 'Software Engineer',
  location: 'Calgary, Alberta',
  quote: 'Lease return was in 3 weeks and I was panicking. These guys saved me. Inspector found zero issues.',
  rating: 5,
  tier: 'Full Basic Detail · $99',
  avatar: "https://images.unsplash.com/photo-1647922608432-5d90eddd5fa4",
  avatarAlt: 'David Kim, young man with glasses smiling outdoors in Seattle',
  featured: false
},
{
  name: 'Priya Nair',
  role: 'Pediatric Nurse',
  location: 'Calgary, Alberta',
  quote: 'Gifted this to my husband for his birthday. He texted me a photo of the car with just "wow." Best $99 I\'ve spent.',
  rating: 5,
  tier: 'Ceramic Shield · $99',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_130a4a3bb-1766831320717.png",
  avatarAlt: 'Priya Nair, woman with dark hair smiling warmly in a hospital corridor',
  featured: false
},
{
  name: 'James Okafor',
  role: 'Real Estate Agent',
  location: 'Calgary, Alberta',
  quote: 'I drive clients around. First impressions matter. YYC Shine on Mondays is now a non-negotiable line item.',
  rating: 5,
  tier: 'Exterior Refresh · $49',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4551b80-1765511200828.png",
  avatarAlt: 'James Okafor, professional man in a blazer smiling confidently',
  featured: false
}];


const trustBadges = [
{ label: '5 / 5', sub: '2,400+ reviews', icon: '⭐' },
{ label: '100%', sub: 'Satisfaction guaranteed', icon: '✓' },
{ label: '30 min', sub: 'Average arrival window', icon: '⏱' }];


export default function Reviews() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    sectionRef?.current?.querySelectorAll('.reveal')?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section id="reviews" className="py-24 px-6" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 reveal">
          <p className="text-xs font-800 uppercase tracking-widest text-blue-400 mb-3">Real customers</p>
          <h2 className="font-fraunces text-4xl md:text-5xl text-white">
            They said it better than we could.
          </h2>
        </div>

        {/* Bento testimonials */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {/* Featured large testimonial */}
          <div
            className="md:col-span-2 bento-card glass-tile-dark reveal p-8 md:p-10 flex flex-col justify-between"
            style={{ minHeight: '280px' }}>
            
            <div className="flex items-start gap-4 mb-6">
              <div className="relative w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 border-2 border-blue-500/20">
                <AppImage
                  src={testimonials?.[0]?.avatar}
                  alt={testimonials?.[0]?.avatarAlt}
                  fill
                  className="object-cover" />
              </div>
              <div>
                <p className="text-white font-700 text-base">{testimonials?.[0]?.name}</p>
                <p className="text-white/45 text-sm font-500">{testimonials?.[0]?.role} · {testimonials?.[0]?.location}</p>
                <div className="flex gap-0.5 mt-1">
                  {Array(testimonials?.[0]?.rating)?.fill(0)?.map((_, j) =>
                  <svg key={j} width="13" height="13" viewBox="0 0 24 24" fill="#3B82F6">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
            <blockquote className="text-white/75 text-lg font-medium leading-relaxed italic mb-6">
              "{testimonials?.[0]?.quote}"
            </blockquote>
            <span className="text-xs font-700 text-blue-300 bg-blue-500/15 px-3 py-1.5 rounded-full w-fit">
              {testimonials?.[0]?.tier}
            </span>
          </div>

          {/* Smaller testimonials */}
          <div className="flex flex-col gap-5">
            {testimonials?.slice(1, 3)?.map((t, i) =>
            <div key={t?.name} className="bento-card glass-tile reveal p-6 flex flex-col gap-3"
            style={{ transitionDelay: `${(i + 1) * 0.08}s` }}>
              
                <div className="flex items-center gap-3">
                  <div className="relative w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                    <AppImage src={t?.avatar} alt={t?.avatarAlt} fill className="object-cover" />
                  </div>
                  <div>
                    <p className="text-white font-700 text-sm">{t?.name}</p>
                    <p className="text-white/45 text-xs font-500">{t?.location}</p>
                  </div>
                </div>
                <p className="text-white/65 text-sm font-medium leading-relaxed">"{t?.quote}"</p>
                <span className="text-xs font-600 text-blue-300 bg-blue-500/12 px-3 py-1 rounded-full w-fit">{t?.tier}</span>
              </div>
            )}
          </div>
        </div>

        {/* Last testimonial + trust badges */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bento-card glass-tile reveal p-6 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <div className="relative w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
                <AppImage src={testimonials?.[3]?.avatar} alt={testimonials?.[3]?.avatarAlt} fill className="object-cover" />
              </div>
              <div>
                <p className="text-white font-700 text-sm">{testimonials?.[3]?.name}</p>
                <p className="text-white/45 text-xs font-500">{testimonials?.[3]?.role} · {testimonials?.[3]?.location}</p>
              </div>
            </div>
            <p className="text-white/65 text-sm font-medium leading-relaxed">"{testimonials?.[3]?.quote}"</p>
            <span className="text-xs font-600 text-blue-300 bg-blue-500/12 px-3 py-1 rounded-full w-fit">{testimonials?.[3]?.tier}</span>
          </div>

          {/* Trust badges */}
          <div className="grid grid-cols-2 gap-3 reveal">
            {trustBadges?.map((badge) =>
            <div key={badge?.label} className="bento-card glass-tile p-5 flex flex-col items-center justify-center text-center gap-1">
                <span className="text-2xl mb-1">{badge?.icon}</span>
                <span className="font-fraunces text-2xl font-700 text-white">{badge?.label}</span>
                <span className="text-xs font-600 text-white/45">{badge?.sub}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>);

}